import { Truck, Route, Package, Gauge, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const services = [
  { icon: Truck, title: "Full-Truckload Freight", desc: "Dedicated and on-demand FTL operations across regional and interstate corridors." },
  { icon: Route, title: "Dedicated Routing", desc: "Lane optimization, dispatch and route engineering for high-frequency commercial customers." },
  { icon: Package, title: "Last-Mile & Drayage", desc: "Port, rail and warehouse drayage with last-mile precision for time-sensitive cargo." },
  { icon: Gauge, title: "Capacity Solutions", desc: "Scalable surge capacity for primes, retailers and federal contract executions." },
  { icon: ShieldCheck, title: "Compliance & Safety", desc: "FMCSA, DOT and federal compliance frameworks engineered into every dispatch." },
];

const capabilities = [
  ["Fleet Coverage", "Multi-State"],
  ["Compliance", "FMCSA / DOT Aligned"],
  ["Diversity", "Minority Woman-Owned"],
  ["Contracts", "Commercial · Government"],
];

export default function Logistics() {
  useSEO({
    title: "Tandem T Logistics — Freight & Transportation",
    description: "Tandem T Logistics — freight, transportation, and supply-chain solutions engineered for multi-state commercial and federal supply chains. A division of Tandem T Holdings LLC.",
    path: "/logistics",
  });
  return (
    <div data-testid="page-logistics">
      <PageHero
        testId="logistics-hero"
        overline="Division 01 — Tandem T Logistics"
        title={<>Freight in <em className="italic">motion.</em> Capacity at <em className="italic">scale.</em></>}
        subtitle="An asset-aware freight and transportation division engineered for the speed, scale and compliance demands of modern enterprise and federal supply chains."
        image="https://images.unsplash.com/photo-1473445730015-841f29a9490b"
      />

      {/* SERVICES */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">Service Lines</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-4 leading-tight">
              Engineered transportation, end-to-end.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 0.06} className="bg-black">
                  <div className="p-10 min-h-[260px] hover:bg-white/[0.03] transition-colors">
                    <Icon size={22} className="text-white" />
                    <h3 className="font-serif font-light text-white text-2xl mt-8">{s.title}</h3>
                    <p className="text-white/65 font-light leading-relaxed mt-4 text-sm">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CAPABILITIES STRIP */}
      <section className="py-24 md:py-28 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-5">
            <p className="overline text-white/55">Capabilities Snapshot</p>
            <h2 className="font-serif font-light text-white text-3xl md:text-4xl mt-4 leading-tight">
              A logistics partner credentialed for the work that matters.
            </h2>
          </Reveal>
          <div className="md:col-span-7 grid grid-cols-2 gap-px bg-white/10">
            {capabilities.map(([k, v]) => (
              <div key={k} className="bg-black p-8">
                <p className="overline text-white/50">{k}</p>
                <p className="font-serif text-white text-2xl mt-3 font-light">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COO LEADERSHIP */}
      <section className="py-24 md:py-32 border-b border-white/10" data-testid="logistics-leadership">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <Reveal className="md:col-span-4">
            <div className="relative max-w-[360px]">
              <img
                src="/images/rohan-taylor.png"
                alt="Rohan Taylor, Chief Operating Officer"
                className="w-full aspect-[4/5] object-contain object-center bw-img bg-black"
                data-testid="coo-portrait"
              />
              <div className="absolute -bottom-3 -right-3 w-full h-full border border-white/20 -z-10" />
            </div>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-8">
            <p className="overline text-white/55">Logistics Leadership</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-5 leading-tight tracking-tight">
              Rohan Taylor
            </h2>
            <p className="overline text-white/55 mt-3">Chief Operating Officer · Head of Logistics</p>
            <p className="text-white/75 font-light leading-relaxed mt-8 text-lg">
              With over six years of dedicated experience in transport operations and precision distribution,
              Rohan Taylor leads our logistics operations with unwavering reliability and compliance. His
              background across commercial transport and high-stakes medical courier services ensures our
              supply chain runs with maximum efficiency, accountability, and care.
            </p>
            <p className="text-white/65 font-light leading-relaxed mt-5">
              As COO, Rohan oversees end-to-end distribution, route optimization, and operational integrity.
              His hands-on leadership guarantees that every grant-funded initiative and community distribution
              project is executed seamlessly, transparently, and on schedule — making our organization a
              trusted partner for impactful investment.
            </p>

            <p className="overline text-white/45 mt-10">Key Focus Areas</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10">
              {[
                ["Operational Excellence", "6+ years managing high-stakes transport and complex route distribution."],
                ["Fiscal & Logistical Stewardship", "Proven commitment to accountability, safe cargo handling, and resource efficiency."],
                ["Mission Execution", "Ensuring community support and vital resources reach recipients on time, every time."],
              ].map(([t, d]) => (
                <div key={t} className="bg-black p-6">
                  <p className="font-serif text-white text-xl font-light leading-snug">{t}</p>
                  <p className="text-white/60 font-light text-sm mt-3 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>

            <p className="text-white/70 font-light leading-relaxed mt-8 border-l border-white/25 pl-6">
              Blending <span className="text-white">precision time management</span>, deep{" "}
              <span className="text-white">regulatory and safety expertise</span>, and{" "}
              <span className="text-white">operational versatility</span> across diverse cargo, Rohan maintains
              an outstanding record of on-time delivery, fleet oversight, and safe operations under pressure —
              adapting seamlessly from heavy transport to precision healthcare dispatch while protecting cargo
              integrity at every turn.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <Reveal>
            <h3 className="font-serif font-light text-white text-3xl md:text-5xl max-w-2xl leading-tight">
              Need capacity that moves with discipline?
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              data-testid="logistics-cta-contact"
              to="/contact?intent=logistics"
              className="group inline-flex items-center gap-3 px-6 py-4 bg-white text-black overline hover:bg-white/85"
            >
              Request a Logistics Brief <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
